import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Paper,
    Typography,
    Stack,
    Skeleton,
    Alert,
    Button,
    Avatar,
    Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import InboxIcon from '@mui/icons-material/Inbox';
import BuildIcon from '@mui/icons-material/Build';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import apiClient from '../api';
import { useAuth } from '../context/AuthContext';
import { canEditServiceTickets, laneTitle, openJobs, unitLabel, truckOf, ageLabel } from '../utils/serviceTickets';

const STATS = [
    { id: 'new', label: 'New', icon: <InboxIcon />, background: 'linear-gradient(135deg, #1565c0 0%, #42a5f5 100%)' },
    { id: 'down', label: 'Down', icon: <ReportProblemIcon />, background: 'linear-gradient(135deg, #b71c1c 0%, #ef5350 100%)' },
    { id: 'in_shop', label: 'In the shop', icon: <BuildIcon />, background: 'linear-gradient(135deg, #E94F37 0%, #ff8a65 100%)' },
    { id: 'waiting_parts', label: 'Waiting on parts', icon: <HourglassEmptyIcon />, background: 'linear-gradient(135deg, #e65100 0%, #ffb74d 100%)' },
    { id: 'thermo_king', label: 'Thermo King', icon: <AcUnitIcon />, background: 'linear-gradient(135deg, #6a1b9a 0%, #ba68c8 100%)' },
];

function ServiceDashboardPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const canEdit = canEditServiceTickets(user?.role);
    const [tickets, setTickets] = useState([]);
    const [ready, setReady] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        let cancelled = false;
        apiClient.get('/service-tickets')
            .then((res) => {
                if (!cancelled) setTickets(res.data?.data || []);
            })
            .catch(() => {
                if (!cancelled) setError('Could not load the shop.');
            })
            .finally(() => {
                if (!cancelled) setReady(true);
            });
        return () => { cancelled = true; };
    }, []);

    const open = tickets.filter((ticket) => ticket.status !== 'closed');
    const counts = {
        new: open.filter((ticket) => ticket.status === 'new').length,
        down: open.filter((ticket) => ticket.down).length,
        in_shop: open.filter((ticket) => ticket.status === 'in_shop').length,
        waiting_parts: open.filter((ticket) => ticket.status === 'waiting_parts').length,
        thermo_king: open.filter((ticket) => ticket.status === 'thermo_king').length,
    };
    const downTrucks = open.filter((ticket) => ticket.down);

    return (
        <Container maxWidth="xl" disableGutters sx={{ pt: 0, pb: 2, px: { xs: 1.5, md: 0 } }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: -0.3 }}>Shop</Typography>
                    <Typography variant="body2" color="text.secondary">What the service department has open</Typography>
                </Box>
                {canEdit && (
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/shop?new=1')}>
                        Add ticket
                    </Button>
                )}
            </Stack>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' }, gap: 1.5, mb: 2 }}>
                {STATS.map((stat) => (
                    <Paper
                        key={stat.id}
                        onClick={() => navigate('/shop')}
                        sx={{
                            background: stat.background,
                            color: 'white',
                            cursor: 'pointer',
                            overflow: 'hidden',
                            transition: 'transform 0.15s',
                            '&:hover': { transform: 'translateY(-2px)' },
                        }}
                    >
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 1.75 }}>
                            <Box>
                                <Typography variant="overline" sx={{ fontWeight: 700, opacity: 0.92, lineHeight: 1.2 }}>{stat.label}</Typography>
                                {ready ? (
                                    <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1 }}>{counts[stat.id]}</Typography>
                                ) : (
                                    <Skeleton variant="text" width={48} height={40} sx={{ bgcolor: 'rgba(255,255,255,0.35)' }} />
                                )}
                            </Box>
                            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 44, height: 44 }}>
                                {stat.icon}
                            </Avatar>
                        </Stack>
                    </Paper>
                ))}
            </Box>

            <Paper sx={{ mb: 2, overflow: 'hidden' }}>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ px: 2, py: 1.25, bgcolor: downTrucks.length ? '#fdecea' : '#e8f5e9' }}
                >
                    <Stack direction="row" spacing={1} alignItems="center">
                        {downTrucks.length ? <ReportProblemIcon sx={{ color: '#c62828' }} /> : <CheckCircleOutlineIcon sx={{ color: '#2e7d32' }} />}
                        <Typography sx={{ fontWeight: 800 }}>Out of service</Typography>
                    </Stack>
                    <Button size="small" onClick={() => navigate('/shop')} sx={{ fontWeight: 700 }}>
                        Open tickets
                    </Button>
                </Stack>
                <Box sx={{ p: 1.5 }}>
                    {!ready ? (
                        <Skeleton variant="rounded" height={64} />
                    ) : downTrucks.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ px: 0.5, py: 0.5 }}>
                            No trucks are down.
                        </Typography>
                    ) : (
                        <Stack spacing={1}>
                            {downTrucks.map((ticket) => (
                                <TicketRow key={ticket._id} ticket={ticket} onOpen={() => navigate(`/shop?ticket=${ticket._id}`)} emphasize />
                            ))}
                        </Stack>
                    )}
                </Box>
            </Paper>

            <Paper sx={{ overflow: 'hidden' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Typography sx={{ fontWeight: 800 }}>Open tickets</Typography>
                    <Chip size="small" label={ready ? open.length : '…'} />
                </Stack>
                <Box sx={{ p: 1.5 }}>
                    {!ready ? (
                        <Skeleton variant="rounded" height={72} />
                    ) : open.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ px: 0.5 }}>Nothing is open.</Typography>
                    ) : (
                        <Stack spacing={1}>
                            {open.map((ticket) => (
                                <TicketRow key={ticket._id} ticket={ticket} onOpen={() => navigate(`/shop?ticket=${ticket._id}`)} />
                            ))}
                        </Stack>
                    )}
                </Box>
            </Paper>
        </Container>
    );
}

function TicketRow({ ticket, onOpen, emphasize }) {
    const jobs = openJobs(ticket);
    const truck = truckOf(ticket);
    const detail = [truck.make, jobs[0]?.text].filter(Boolean).join(' · ');
    return (
        <Paper
            variant="outlined"
            onClick={onOpen}
            sx={{
                px: 1.5,
                py: 1.25,
                cursor: 'pointer',
                borderColor: emphasize ? '#ef9a9a' : 'divider',
                borderLeft: '4px solid',
                borderLeftColor: emphasize ? '#c62828' : (ticket.unread ? '#1976d2' : '#E94F37'),
                '&:hover': { bgcolor: 'rgba(233, 79, 55, 0.04)' },
            }}
        >
            <Stack direction="row" alignItems="center" spacing={1.5}>
                <Avatar sx={{ bgcolor: emphasize ? '#c62828' : '#1c1917', width: 40, height: 40 }}>
                    <LocalShippingIcon fontSize="small" />
                </Avatar>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontWeight: 800, lineHeight: 1.2 }} noWrap>{unitLabel(ticket)}</Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                        {detail || 'No jobs yet'} · {ageLabel(ticket)}
                    </Typography>
                </Box>
                {ticket.unread && <Chip size="small" color="info" label="New" sx={{ fontWeight: 700 }} />}
                <Chip size="small" label={laneTitle(ticket.status)} sx={{ fontWeight: 700 }} />
                {ticket.down && <Chip size="small" color="error" label="Down" />}
            </Stack>
        </Paper>
    );
}

export default ServiceDashboardPage;
